import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import Sellerimg from "../img/Sellerimg.svg";
import api from "../../api/axiosInstance";

const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [newPasswordError, setNewPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useParams();

  const getFirstPasswordError = (password) => {
    if (password.length < 8) {
      return "Your password must contain minimum of 8 characters.";
    }
    if (!/[A-Z]/.test(password)) {
      return "Your password must contain at least one uppercase letter.";
    }
    if (!/[a-z]/.test(password)) {
      return "Your password must contain at least one lowercase letter.";
    }
    if (!/[!@#$%^&*(),.?\":{}|<>]/.test(password)) {
      return "Your password must contain at least one special character. (e.g., @, #, $, etc.)";
    }
    return "";
  };

  const handleSubmit = async () => {
    let isValid = true;

    // Reset errors
    setNewPasswordError("");
    setConfirmPasswordError("");

    if (!newPassword) {
      setNewPasswordError("Enter your new password.");
      isValid = false;
    } else {
      const firstError = getFirstPasswordError(newPassword);
      if (firstError) {
        setNewPasswordError(firstError);
        isValid = false;
      }
    }

    if (!confirmPassword) {
      setConfirmPasswordError("Enter your confirm password.");
      isValid = false;
    } else if (newPassword !== confirmPassword) {
      setConfirmPasswordError(
        "Entered confirm password does not match the new password"
      );
      isValid = false;
    }

    if (!isValid) return;

    if (!newPassword || !confirmPassword) {
      alert("Please fill in both fields.");
      return;
    }

    try {
      const response = await api.post(
        "/display/api/v1/complete-forget-password",
        {
          token: token,
          new_password: newPassword,
        }
      );

      if (response.data.status === "success") {
        setShowSuccessModal(true);
      } else {
        alert("Failed: " + response.data.message);
      }
    } catch (err) {
      console.error(err);
      alert("An error occurred. Please try again.");
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row  px-5">
      {/* Left Content */}

      <div
        className="w-full  lg:w-1/2 min-h-[300px] lg:min-h-screen bg-no-repeat bg-center bg-cover"
        style={{
          backgroundImage: `url(${Sellerimg})`,
          backgroundSize: "contain",
        }}
      ></div>

      {/* Right Content (Background Image) */}
      <div className="w-full lg:w-1/2 flex justify-center items-center p-6">
        <div className="w-full sm:w-3/4  h-auto flex flex-col justify-center p-6 rounded-md bg-white">
          <h1 className="font-inter text-3xl font-semibold mb-[30px] text-center">
            Reset Password
          </h1>

          {/* Enter New Password */}
          <div className="mb-4">
            <label className="block text-sm mb-1 font-normal font-inter">
              New Password<span className="text-red-500">*</span>
            </label>
            <div className="relative">
              {/* Right side Eye Icon */}
              <span
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-black cursor-pointer"
                onClick={() => setShowNewPassword((prev) => !prev)}
              >
                {showNewPassword ? <Eye size={20} /> : <EyeOff size={20} />}
              </span>
              <input
                type={showNewPassword ? "text" : "password"}
                className="w-full px-4 py-2 border-2 text-sm border-[#9061DB] bg-transparent rounded-md font-inter focus:outline-none focus:ring-2 focus:ring-[#9061DB]"
                placeholder="Enter your new password"
                onChange={(e) => {
                  setNewPasswordError("");
                  setNewPassword(e.target.value);
                }}
              />
            </div>
            {newPasswordError && (
              <p className="text-sm text-red-500 mt-1">{newPasswordError}</p>
            )}
          </div>

          {/* Confirm New Password */}
          <div className="mb-[30px]">
            <label className="block text-sm mb-1 font-normal font-inter">
              Confirm New Password<span className="text-red-500">*</span>
            </label>
            <div className="relative">
              {/* Right side Eye Icon */}
              <span
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-black cursor-pointer"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
              >
                {showConfirmPassword ? <Eye size={20} /> : <EyeOff size={20} />}
              </span>
              <input
                type={showConfirmPassword ? "text" : "password"}
                className="w-full px-4 py-2 border-2 text-sm border-[#9061DB] bg-transparent rounded-md font-inter focus:outline-none focus:ring-2 focus:ring-[#9061DB]"
                placeholder="Enter your confirm new password"
                onChange={(e) => {
                  setConfirmPasswordError("");
                  setConfirmPassword(e.target.value);
                }}
              />
            </div>
            {confirmPasswordError && (
              <p className="text-sm text-red-500 mt-1">
                {confirmPasswordError}
              </p>
            )}
          </div>

          <div className="flex justify-between items-end">
            <span
              className="text-sm cursor-pointer"
              onClick={() => navigate("/")}
            >
              Back to{" "}
              <span className="text-sm font-semibold text-[#9367D7]">
                Login
              </span>{" "}
            </span>

            <button
              type="submit"
              onClick={handleSubmit}
              // onClick={()=>{navigate("/changePassword")}}
              className="bg-primary text-sm text-white px-6 py-2 rounded-md font-inter hover:scale-105 transition-transform duration-300 sm:w-auto"
              style={{ willChange: "transform" }}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>

      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-md shadow-lg max-w-md w-full">
            <h2 className="text-xl font-semibold text-center mb-4">Success</h2>
            <p className="text-center text-gray-700 mb-6">
              Your password has been successfully reset.
            </p>
            <div className="flex justify-center">
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate("/");
                }}
                className="bg-[#9061DB] text-white px-4 py-2 rounded hover:bg-[#7c4ed4]"
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResetPassword;
